"use client";

import { CircleAlert, Headset, RefreshCw } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Button, Link, List, Stack, Text } from "@leabankab/component-library";
import { StepContainer } from "@/components/layout";
import { Heading } from "@/components/ui";
import { type SupportedLocale, getLocaleConfig } from "@/config/locale-config";
import { SigningErrorIcon } from "./signing-error-icon";

type Props = {
	errorCode?: string;
	onTryAgain: () => void;
	/** A retry is already redirecting to the identity provider. */
	isRetrying?: boolean;
};

export const SigningFailed = ({
	errorCode,
	onTryAgain,
	isRetrying = false,
}: Props) => {
	const t = useTranslations("pages.SignCallbackPage");
	const locale = useLocale() as SupportedLocale;
	const { supportPhone: phone, supportPhoneHref } = getLocaleConfig(locale);
	const whatYouCanDo = t.raw("signingFailed.whatYouCanDo") as string[];
	const errorDescriptions = t.raw("signingFailed.errorDescriptions") as Record<
		string,
		string
	>;

	const resolvedCode = errorCode ?? "UNKNOWN";
	// The default description repeats the page's own copy, so only a code's own one is shown.
	const codeDescription =
		resolvedCode === "default" ? undefined : errorDescriptions[resolvedCode];

	const actionIcons = [
		<RefreshCw key="retry" size={20} aria-hidden="true" />,
		<Headset key="support" size={20} aria-hidden="true" />,
	];

	return (
		<StepContainer>
			<Stack gap={3} align="stretch">
				<Stack gap={2} align="center" className="text-center text-balance">
					<SigningErrorIcon>
						<CircleAlert size={40} strokeWidth={2} aria-hidden="true" />
					</SigningErrorIcon>
					<Heading level={1} textStyle="heading-lg">
						{t("signingFailed.title")}
					</Heading>
					<Text color="text.subtle">{t("signingFailed.description")}</Text>
				</Stack>

				<Stack gap={1} className="rounded-xl bg-bg-subtle p-2.5">
					<Text textStyle="body-sm" fontWeight="medium" color="text.base">
						{t("signingFailed.errorCodeLabel", { code: resolvedCode })}
					</Text>
					{codeDescription && (
						<Text textStyle="body-sm" color="text.base">
							{codeDescription}
						</Text>
					)}
				</Stack>

				<Stack gap={1.5}>
					<Heading level={2} textStyle="heading-xs">
						{t("signingFailed.whatYouCanDoTitle")}
					</Heading>
					<List.Root className="bg-transparent p-0">
						{whatYouCanDo.map((item, index) => (
							<List.Item key={item} icon={actionIcons[index]}>
								{item.replace("{phone}", phone)}
							</List.Item>
						))}
					</List.Root>
				</Stack>

				<Stack gap={1.5} align="center">
					<Button
						size="lg"
						onClick={onTryAgain}
						disabled={isRetrying}
						aria-busy={isRetrying}
						className="w-full"
					>
						{t("signingFailed.tryAgain")}
					</Button>
					<Link href={supportPhoneHref} color="primary" className="font-medium">
						{t("signingFailed.contactSupport")}
					</Link>
				</Stack>
			</Stack>
		</StepContainer>
	);
};
